import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleIcon from '@mui/icons-material/Circle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import type { Procurement } from '../../types/procurement';

interface ProcurementActionStepperProps {
  procurement: Procurement;
  onStepClick?: (stepIndex: number) => void;
}

const steps = [
  { id: 'DRAFT', label: 'Draft / Setup', index: 0 },
  { id: 'PUBLISHED', label: 'Open / Submissions', index: 1 },
  { id: 'EVALUATION', label: 'Evaluation', index: 2 },
  { id: 'AWARDED', label: 'Awarded', index: 3 },
];

const getStatusIndex = (status: string) => {
  if (status === 'ARCHIVED') {
    return 4; // Beyond completed
  }
  const step = steps.find((s) => s.id === status);
  return step ? step.index : 0;
};

const ProcurementActionStepper = ({ procurement, onStepClick }: ProcurementActionStepperProps) => {
  const theme = useTheme();
  const currentStepIndex = getStatusIndex(procurement.status);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        width: '100%',
        justifyContent: 'space-between',
        position: 'relative',
        px: 1,
      }}
    >
      {/* Connector Line */}
      <Box
        sx={{
          position: 'absolute',
          top: '18px', // Center of the 36px icon button (approx)
          left: '10%',
          right: '10%',
          height: '2px',
          bgcolor: theme.palette.grey[300],
          zIndex: 0,
        }}
      />

      {steps.map((s) => {
        const isPassed = currentStepIndex > s.index;
        const isCurrent = currentStepIndex === s.index;

        let StepIcon = RadioButtonUncheckedIcon;
        let color = theme.palette.grey[400];
        let tooltip = s.label;
        let borderColor = theme.palette.background.paper;

        if (isPassed) {
          StepIcon = CheckCircleIcon;
          color = '#2e7d32'; // completed green
          tooltip = `${s.label} (Completed)`;
        } else if (isCurrent) {
          StepIcon = CircleIcon;
          // Use a custom property if available or primary main
          color = theme.palette.primary.main;
          tooltip = `${s.label} (Current)`;
          borderColor = color;
        } else {
          // Future Step
          StepIcon = RadioButtonUncheckedIcon;
          color = theme.palette.grey[400];
          tooltip = `${s.label} (Pending)`;
        }

        return (
          <Box
            key={s.id}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              zIndex: 1,
              position: 'relative',
              width: '25%', // 4 steps
            }}
          >
            <Tooltip title={tooltip}>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  if (onStepClick) {
                    onStepClick(s.index);
                  }
                }}
                sx={{
                  p: 0.5,
                  bgcolor: theme.palette.background.paper,
                  border: `2px solid ${borderColor}`,
                  '&:hover': { bgcolor: theme.palette.grey[100] },
                  width: 36,
                  height: 36,
                }}
              >
                <StepIcon sx={{ color, fontSize: '24px' }} />
              </IconButton>
            </Tooltip>

            <Typography
              variant="caption"
              sx={{
                fontSize: '0.75rem',
                color: isCurrent ? theme.palette.text.primary : theme.palette.text.secondary,
                fontWeight: isCurrent ? 700 : 400,
                mt: 0.5,
                textAlign: 'center',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}
            >
              {s.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default ProcurementActionStepper;
